import { launchVelocityFromImpulse } from '../../services/damageModel';
import type { ProfileId } from '../../services/destructionProfiles';
import type {
  CanonicalDemolitionEvent,
  CanonicalDemolitionEventUnion,
  DemolitionEventKind,
  DemolitionEventPayloadMap,
} from './demolitionEventRing';
import type {
  DemoTool,
  DemolitionImpact,
  DemolitionRuntime,
  ImpactReleaseSample,
  ImpactTrace,
} from './types';

export function appendDemolitionEvent<K extends DemolitionEventKind>(
  rt: DemolitionRuntime,
  kind: K,
  payload: DemolitionEventPayloadMap[K],
): CanonicalDemolitionEvent<K> {
  return rt.eventRing.append(kind, payload);
}

/** Pull the same canonical records that the runtime used to expose as pending arrays. */
export function drainDemolitionEvents(rt: DemolitionRuntime): readonly CanonicalDemolitionEventUnion[] {
  return rt.eventRing.drain();
}

export function copyReleaseSample(release: ImpactReleaseSample): ImpactReleaseSample {
  return {
    id: release.id,
    position: { ...release.position },
    ...launchVelocityFromImpulse(release),
  };
}

export function pushImpactTrace(
  rt: DemolitionRuntime,
  trace: Omit<ImpactTrace, 'id' | 'epoch' | 'sequence' | 'material'> & { material?: ProfileId },
) {
  appendDemolitionEvent(rt, 'impact-trace', {
    ...trace,
    material: rt.profile.id,
    directReleases: trace.directReleases.map(copyReleaseSample),
    supportReleases: trace.supportReleases.map(copyReleaseSample),
  });
}

export function recordImpact(rt: DemolitionRuntime, impact: DemolitionImpact) {
  rt.lastImpact = { ...impact };
  appendDemolitionEvent(rt, 'impact', { ...impact });
}

export function queueImpactBurst(
  rt: DemolitionRuntime,
  point: { x: number; y: number; z: number },
  projectileToOther: { x: number; y: number; z: number },
  speed: number,
  weapon: DemoTool,
  pulverizedVolume = 0,
) {
  const length = Math.hypot(projectileToOther.x, projectileToOther.y, projectileToOther.z) || 1;
  // Contact direction points into the contacted body; the visual normal points
  // back toward the projectile so the burst is visible on the struck surface.
  const nx = -projectileToOther.x / length;
  const ny = -projectileToOther.y / length;
  const nz = -projectileToOther.z / length;
  appendDemolitionEvent(rt, 'impact-burst', {
    x: point.x,
    y: point.y,
    z: point.z,
    radius: Math.min(0.5, Math.max(0.12, 0.08 + speed / 55 * 0.32)),
    normal: { x: nx, y: ny, z: nz },
    weapon,
    material: rt.profile.id,
    pulverizedVolume,
  });
}
